/**
 * Loan Tape Extraction form — the inline card shown when the user @-mentions the
 * "Loan Tape Extraction" template. Collects three workbook slots:
 *
 *   source_file (training raw) + target_file (training transformed)  -> learn
 *   source_file (new raw)                                            -> apply
 *
 * Each slot is a document picker backed by the same catalog mention-search
 * endpoint the chat input uses. On submit it hands the three doc refs back to
 * the chat, which sends them as structured stream metadata.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import type { CatalogDocument } from "../hooks/useDocumentMention";
import { apiGet } from "../utils/apiClient";

export interface SchemaExtractorDocRef {
	doc_id: string;
	filename?: string;
	deal_name?: string;
	project_id?: string | null;
}

/**
 * Row-level filter operators, using Jira/Notion-style vocabulary. `is_empty` /
 * `is_not_empty` are unary (no value input).
 */
export type FilterOperator =
	| "is"
	| "is_not"
	| "contains"
	| "not_contains"
	| "gt"
	| "lt"
	| "between"
	| "any_of"
	| "is_empty"
	| "is_not_empty";

export interface FilterCondition {
	id: string;
	column: string;
	operator: FilterOperator;
	value: string;
}

/**
 * One filter group scoped to a file. `applies_to` is a file's doc_id or the
 * sentinel "all". `columns` is the column-level slice (keep only these);
 * `conditions` are the row-level filters joined by `join`.
 */
export interface FilterGroup {
	id: string;
	applies_to: string;
	applies_to_label: string;
	join: "AND" | "OR";
	columns: string[];
	conditions: FilterCondition[];
}

export interface SchemaExtractorPayload {
	training_source: SchemaExtractorDocRef;
	training_target: SchemaExtractorDocRef;
	new_source: SchemaExtractorDocRef;
	/** Name for the generated output workbook (defaults from new_source). */
	output_filename?: string;
	/**
	 * Optional per-file column/row filters. UI-only for now — carried in the
	 * payload but not yet consumed by the backend.
	 */
	filters?: FilterGroup[];
}

const OPERATORS: { value: FilterOperator; label: string; unary?: boolean }[] = [
	{ value: "is", label: "is" },
	{ value: "is_not", label: "is not" },
	{ value: "contains", label: "contains" },
	{ value: "not_contains", label: "does not contain" },
	{ value: "gt", label: "greater than" },
	{ value: "lt", label: "less than" },
	{ value: "between", label: "between" },
	{ value: "any_of", label: "is any of" },
	{ value: "is_empty", label: "is empty", unary: true },
	{ value: "is_not_empty", label: "is not empty", unary: true },
];

const UNARY_OPS = new Set<FilterOperator>(["is_empty", "is_not_empty"]);

// Module-level counter for stable ids on filter groups/conditions (no crypto
// needed — these ids only live for the lifetime of the open form).
let _filterIdSeq = 0;
const nextFilterId = (): string => `flt-${++_filterIdSeq}`;

const stem = (filename?: string): string =>
	filename ? filename.replace(/\.[^.]+$/, "") : "";

type SlotKey = "training_source" | "training_target" | "new_source";

interface SlotDef {
	key: SlotKey;
	heading: string;
	roleLabel: string;
	description: string;
}

const SLOTS: SlotDef[] = [
	{
		key: "training_source",
		heading: "source_rawdata",
		roleLabel: "Training source (raw)",
		description: "The file that contains the raw data.",
	},
	{
		key: "training_target",
		heading: "target_raw_data",
		roleLabel: "Training loan tape (transformed)",
		description: "The file that contains the loan tape of the source file.",
	},
	{
		key: "new_source",
		heading: "source_loan_tape",
		roleLabel: "New source (raw)",
		description:
			"The file that contains new raw data that will be transformed into clean loan tape.",
	},
];

const toRef = (doc: CatalogDocument): SchemaExtractorDocRef => ({
	doc_id: doc.doc_id,
	filename: doc.filename,
	deal_name: doc.deal_name,
	project_id: doc.project_id ?? undefined,
});

interface SlotPickerProps {
	slot: SlotDef;
	selected: CatalogDocument | null;
	onSelect: (doc: CatalogDocument | null) => void;
}

const SlotPicker: React.FC<SlotPickerProps> = ({ slot, selected, onSelect }) => {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<CatalogDocument[]>([]);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [highlighted, setHighlighted] = useState(0);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const listRef = useRef<HTMLUListElement>(null);

	const selectDoc = useCallback(
		(doc: CatalogDocument) => {
			onSelect(doc);
			setOpen(false);
			setQuery("");
		},
		[onSelect],
	);

	// New result set → highlight the first row.
	useEffect(() => {
		setHighlighted(0);
	}, [results]);

	// Keep the highlighted row in view as arrow keys move it.
	useEffect(() => {
		const options =
			listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
		options?.[highlighted]?.scrollIntoView({ block: "nearest" });
	}, [highlighted]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (!open || results.length === 0) return;
			switch (e.key) {
				case "ArrowDown":
					e.preventDefault();
					setHighlighted((p) => (p < results.length - 1 ? p + 1 : 0));
					break;
				case "ArrowUp":
					e.preventDefault();
					setHighlighted((p) => (p > 0 ? p - 1 : results.length - 1));
					break;
				case "Enter":
				case "Tab":
					e.preventDefault();
					selectDoc(results[highlighted]);
					break;
				case "Escape":
					e.preventDefault();
					setOpen(false);
					break;
				default:
					break;
			}
		},
		[open, results, highlighted, selectDoc],
	);

	useEffect(() => {
		if (!open) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			setLoading(true);
			try {
				const encoded = encodeURIComponent(query.trim());
				const endpoint = encoded
					? `/api/v3/catalog/mention-search?q=${encoded}&limit=10`
					: `/api/v3/catalog/mention-search?limit=10`;
				const docs = await apiGet<CatalogDocument[]>(endpoint);
				// Documents only — a schema-extractor slot is one workbook.
				setResults(docs.filter((d) => d.ref_type !== "deal"));
			} catch {
				setResults([]);
			} finally {
				setLoading(false);
			}
		}, 200);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [query, open]);

	return (
		<div
			className={`relative border border-gray-200 rounded-lg p-3 ${
				open && !selected ? "z-20" : ""
			}`}
		>
			<div className="flex items-baseline gap-2">
				<span className="font-mono text-sm font-semibold text-[#004C4B]">
					{slot.heading}
				</span>
				<span className="text-xs text-gray-400">· {slot.roleLabel}</span>
			</div>
			<p className="text-xs text-gray-500 mt-0.5">{slot.description}</p>

			{selected ? (
				<div className="mt-2 flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1.5">
					<span className="text-sm truncate">{selected.filename}</span>
					<button
						type="button"
						className="text-gray-400 hover:text-gray-600 flex-shrink-0"
						onClick={() => {
							onSelect(null);
							setOpen(true);
						}}
						aria-label="Clear selection"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
			) : (
				<div className="mt-2 relative">
					<input
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onFocus={() => setOpen(true)}
						onKeyDown={handleKeyDown}
						// Delay so an item's onMouseDown selection registers before close.
						onBlur={() => setTimeout(() => setOpen(false), 150)}
						placeholder="Search workbooks…"
						className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#008782]"
					/>
					{open && (
						<ul
							ref={listRef}
							className="absolute z-30 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg py-1 shadow-md"
							role="listbox"
						>
							{loading && (
								<li className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
									<Loader2 className="w-3 h-3 animate-spin" /> Searching…
								</li>
							)}
							{!loading && results.length === 0 && (
								<li className="px-3 py-2 text-xs text-gray-400">
									No workbooks found
								</li>
							)}
							{results.map((doc, idx) => (
								<li
									key={doc.doc_id}
									role="option"
									aria-selected={idx === highlighted}
									className={`px-3 py-2 cursor-pointer text-sm ${
										idx === highlighted ? "bg-[#004C4B1A]" : "hover:bg-gray-50"
									}`}
									onMouseEnter={() => setHighlighted(idx)}
									onMouseDown={(e) => {
										e.preventDefault();
										selectDoc(doc);
									}}
								>
									<span className="font-medium truncate block">
										{doc.filename}
									</span>
									<span className="text-xs text-gray-500 truncate block">
										{doc.deal_name}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
};

// ── Column chip input (column-level slice) ──────────────────────────────

interface ColumnChipsProps {
	columns: string[];
	onChange: (columns: string[]) => void;
}

const ColumnChips: React.FC<ColumnChipsProps> = ({ columns, onChange }) => {
	const [draft, setDraft] = useState("");

	const commit = () => {
		const val = draft.trim();
		if (val && !columns.includes(val)) onChange([...columns, val]);
		setDraft("");
	};

	return (
		<div className="mt-1 flex flex-wrap items-center gap-1.5 border border-gray-200 rounded px-2 py-1.5 focus-within:ring-1 focus-within:ring-[#008782]">
			{columns.map((col) => (
				<span
					key={col}
					className="inline-flex items-center gap-1 bg-[#004C4B1A] text-[#004C4B] text-xs rounded px-1.5 py-0.5"
				>
					{col}
					<button
						type="button"
						onClick={() => onChange(columns.filter((c) => c !== col))}
						className="text-[#004C4B]/60 hover:text-[#004C4B]"
						aria-label={`Remove column ${col}`}
					>
						<X className="w-3 h-3" />
					</button>
				</span>
			))}
			<input
				type="text"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === ",") {
						e.preventDefault();
						commit();
					} else if (e.key === "Backspace" && draft === "" && columns.length) {
						onChange(columns.slice(0, -1));
					}
				}}
				onBlur={commit}
				placeholder={columns.length ? "" : "Add column…"}
				className="flex-1 min-w-[6rem] text-sm outline-none bg-transparent"
			/>
		</div>
	);
};

// ── Per-file filter builder (row + column slicing) ──────────────────────

interface FilterBuilderProps {
	groups: FilterGroup[];
	onChange: (groups: FilterGroup[]) => void;
	fileOptions: { value: string; label: string }[];
}

const FilterBuilder: React.FC<FilterBuilderProps> = ({
	groups,
	onChange,
	fileOptions,
}) => {
	// Scroll the newly added rule into view when the rule count grows (only on
	// add — not on edits/removals — so the modal follows the "+ Add" action).
	const endRef = useRef<HTMLDivElement>(null);
	const prevCountRef = useRef(groups.length);
	useEffect(() => {
		if (groups.length > prevCountRef.current) {
			endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
		}
		prevCountRef.current = groups.length;
	}, [groups.length]);

	const patchGroup = (id: string, patch: Partial<FilterGroup>) =>
		onChange(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)));

	const patchCondition = (
		groupId: string,
		condId: string,
		patch: Partial<FilterCondition>,
	) =>
		onChange(
			groups.map((g) =>
				g.id === groupId
					? {
							...g,
							conditions: g.conditions.map((c) =>
								c.id === condId ? { ...c, ...patch } : c,
							),
						}
					: g,
			),
		);

	const addGroup = () => {
		const firstFile = fileOptions[0] ?? { value: "all", label: "All source files" };
		onChange([
			...groups,
			{
				id: nextFilterId(),
				applies_to: firstFile.value,
				applies_to_label: firstFile.label,
				join: "AND",
				columns: [],
				conditions: [
					{ id: nextFilterId(), column: "", operator: "is", value: "" },
				],
			},
		]);
	};

	return (
		<div className="mt-2 space-y-2">
			{groups.map((group, gi) => (
				<div
					key={group.id}
					className="border border-gray-200 rounded-md p-2.5 bg-gray-50/60"
				>
					<div className="flex items-center justify-between gap-2">
						<div className="flex items-center gap-1.5 text-xs text-gray-500">
							<span className="font-medium text-gray-600">
								Rule {gi + 1}
							</span>
							<span>· applies to</span>
							<select
								value={group.applies_to}
								onChange={(e) => {
									const opt = fileOptions.find(
										(o) => o.value === e.target.value,
									);
									patchGroup(group.id, {
										applies_to: e.target.value,
										applies_to_label: opt?.label ?? e.target.value,
									});
								}}
								className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#008782]"
							>
								{fileOptions.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</div>
						<button
							type="button"
							onClick={() => onChange(groups.filter((g) => g.id !== group.id))}
							className="text-gray-400 hover:text-gray-600 flex-shrink-0"
							aria-label="Remove rule"
						>
							<X className="w-4 h-4" />
						</button>
					</div>

					{/* Column-level slice */}
					<div className="mt-2">
						<label className="text-xs text-gray-500">Include columns</label>
						<ColumnChips
							columns={group.columns}
							onChange={(cols) => patchGroup(group.id, { columns: cols })}
						/>
					</div>

					{/* Row-level filters */}
					<div className="mt-2">
						<label className="text-xs text-gray-500">Where</label>
						<div className="mt-1 space-y-1.5">
							{group.conditions.map((cond, ci) => (
								<div key={cond.id} className="flex items-center gap-1.5">
									{ci === 0 ? (
										<span className="w-10 flex-shrink-0" aria-hidden="true" />
									) : (
										<button
											type="button"
											onClick={() =>
												patchGroup(group.id, {
													join: group.join === "AND" ? "OR" : "AND",
												})
											}
											className="text-xs font-medium text-[#008782] w-10 flex-shrink-0 text-left hover:underline"
										>
											{group.join}
										</button>
									)}
									<input
										type="text"
										value={cond.column}
										onChange={(e) =>
											patchCondition(group.id, cond.id, {
												column: e.target.value,
											})
										}
										placeholder="Column"
										className="flex-1 min-w-0 text-sm border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#008782]"
									/>
									<select
										value={cond.operator}
										onChange={(e) =>
											patchCondition(group.id, cond.id, {
												operator: e.target.value as FilterOperator,
											})
										}
										className="text-sm border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#008782]"
									>
										{OPERATORS.map((op) => (
											<option key={op.value} value={op.value}>
												{op.label}
											</option>
										))}
									</select>
									{!UNARY_OPS.has(cond.operator) && (
										<input
											type="text"
											value={cond.value}
											onChange={(e) =>
												patchCondition(group.id, cond.id, {
													value: e.target.value,
												})
											}
											placeholder="Value"
											className="flex-1 min-w-0 text-sm border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#008782]"
										/>
									)}
									<button
										type="button"
										onClick={() =>
											patchGroup(group.id, {
												conditions:
													group.conditions.length > 1
														? group.conditions.filter(
																(c) => c.id !== cond.id,
															)
														: group.conditions,
											})
										}
										className="text-gray-400 hover:text-gray-600 flex-shrink-0 disabled:opacity-30"
										disabled={group.conditions.length <= 1}
										aria-label="Remove condition"
									>
										<X className="w-4 h-4" />
									</button>
								</div>
							))}
						</div>
						<button
							type="button"
							onClick={() =>
								patchGroup(group.id, {
									conditions: [
										...group.conditions,
										{
											id: nextFilterId(),
											column: "",
											operator: "is",
											value: "",
										},
									],
								})
							}
							className="mt-1.5 text-xs font-medium text-[#008782] hover:underline"
						>
							+ Add condition
						</button>
					</div>
				</div>
			))}

			<button
				type="button"
				onClick={addGroup}
				className="text-xs font-medium text-[#008782] hover:underline"
			>
				+ Add filter rule{groups.length ? " for another file" : ""}
			</button>
			<div ref={endRef} aria-hidden="true" />
		</div>
	);
};

// ── Shared form body ────────────────────────────────────────────────────

/** Same shape as the KPI composer's section headings, so the two read alike. */
const SectionHeading: React.FC<{ title: string; hint?: string }> = ({
	title,
	hint,
}) => (
	<div className="mb-2">
		<h3 className="text-base font-medium text-text-base">{title}</h3>
		{hint && <p className="text-xs text-text-muted">{hint}</p>}
	</div>
);

export interface SchemaExtractorFieldsProps {
	onSubmit: (payload: SchemaExtractorPayload) => void;
	onCancel: () => void;
}

/**
 * The Loan Tape Extraction form body — slots, output name, filters, actions —
 * styled to match the Extract KPIs composer. Hosted two ways: inline on the
 * chat @-mention modal below. Loan tape workflows use LoanTape/LoanTapeSetupForm.
 */
export const SchemaExtractorFields: React.FC<SchemaExtractorFieldsProps> = ({
	onSubmit,
	onCancel,
}) => {
	const [selections, setSelections] = useState<
		Record<SlotKey, CatalogDocument | null>
	>({ training_source: null, training_target: null, new_source: null });
	const [outputName, setOutputName] = useState("");
	const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);

	// Files the user can scope a filter rule to: any selected slot, plus an
	// "all files" catch-all. Recomputed as slots are filled/cleared.
	const fileOptions = React.useMemo(() => {
		const opts: { value: string; label: string }[] = [
			{ value: "all", label: "All source files" },
		];
		for (const key of ["training_source", "new_source", "training_target"] as const) {
			const doc = selections[key];
			if (doc) opts.push({ value: doc.doc_id, label: doc.filename });
		}
		return opts;
	}, [selections]);

	const setSlot = useCallback(
		(key: SlotKey, doc: CatalogDocument | null) => {
			setSelections((prev) => ({ ...prev, [key]: doc }));
		},
		[],
	);

	const allChosen =
		!!selections.training_source &&
		!!selections.training_target &&
		!!selections.new_source;

	const defaultOutput = selections.new_source
		? `${stem(selections.new_source.filename)} - Loan Tape.xlsx`
		: "";

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!allChosen) return;
		// Drop empty conditions and any group left with no slice and no filter.
		const cleanedFilters = filterGroups
			.map((g) => ({
				...g,
				conditions: g.conditions.filter(
					(c) =>
						c.column.trim() !== "" &&
						(UNARY_OPS.has(c.operator) || c.value.trim() !== ""),
				),
			}))
			.filter((g) => g.columns.length > 0 || g.conditions.length > 0);
		onSubmit({
			training_source: toRef(selections.training_source!),
			training_target: toRef(selections.training_target!),
			new_source: toRef(selections.new_source!),
			output_filename: outputName.trim() || undefined,
			filters: cleanedFilters.length > 0 ? cleanedFilters : undefined,
		});
		setSelections({ training_source: null, training_target: null, new_source: null });
		setOutputName("");
		setFilterGroups([]);
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<section>
				<SectionHeading
					title="Training pair"
					hint="A raw workbook and the clean loan tape built from it — the pair the mapping is learned from."
				/>
				<div className="space-y-3">
					{SLOTS.filter((slot) => slot.key !== "new_source").map((slot) => (
						<SlotPicker
							key={slot.key + slot.heading}
							slot={slot}
							selected={selections[slot.key]}
							onSelect={(doc) => setSlot(slot.key, doc)}
						/>
					))}
				</div>
			</section>

			<section>
				<SectionHeading
					title="New workbook"
					hint="The raw workbook the learned mapping is applied to."
				/>
				{SLOTS.filter((slot) => slot.key === "new_source").map((slot) => (
					<SlotPicker
						key={slot.key + slot.heading}
						slot={slot}
						selected={selections[slot.key]}
						onSelect={(doc) => setSlot(slot.key, doc)}
					/>
				))}
			</section>

			<section>
				<SectionHeading
					title="Output"
					hint="Name for the generated loan tape workbook."
				/>
				<input
					type="text"
					value={outputName}
					onChange={(e) => setOutputName(e.target.value)}
					placeholder={defaultOutput || "e.g. My Deal - Loan Tape.xlsx"}
					className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base text-text-base focus:outline-none focus:ring-1 focus:ring-[#008782]"
				/>
			</section>

			<section>
				<SectionHeading
					title="Filters & slicing"
					hint="Optional — keep only a slice of columns and filter rows, with a rule per file."
				/>
				<FilterBuilder
					groups={filterGroups}
					onChange={setFilterGroups}
					fileOptions={fileOptions}
				/>
			</section>

			<div className="flex items-center gap-2">
				<button
					type="submit"
					disabled={!allChosen}
					className="inline-flex items-center gap-2 rounded-lg bg-[#008782] px-4 py-2 text-sm font-medium text-white hover:bg-[#004C4B] disabled:opacity-60"
				>
					Run Loan Tape Extraction
				</button>
				<button
					type="button"
					onClick={onCancel}
					className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-text-base hover:bg-gray-50"
				>
					Cancel
				</button>
			</div>
		</form>
	);
};

// ── Chat modal wrapper (the @-mention flow) ─────────────────────────────

interface SchemaExtractorFormProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (payload: SchemaExtractorPayload) => void;
}

const SchemaExtractorForm: React.FC<SchemaExtractorFormProps> = ({
	open,
	onClose,
	onSubmit,
}) => {
	if (!open) return null;

	return (
		<div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/30 p-4">
			<div className="w-full max-w-lg max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-xl p-5">
				<div className="flex items-start justify-between flex-shrink-0">
					<div>
						<h2 className="text-lg font-semibold text-text-strong">
							Loan Tape Extraction
						</h2>
						<p className="text-sm text-text-muted mt-0.5">
							Learn the raw → clean loan tape mapping from a completed pair,
							then apply it to a new raw workbook.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600"
						aria-label="Close"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="mt-4 flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
					<SchemaExtractorFields onSubmit={onSubmit} onCancel={onClose} />
				</div>
			</div>
		</div>
	);
};

export default SchemaExtractorForm;
